import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Dumbbell, Library, MessageSquare, ClipboardCheck, BarChart3, Settings as SettingsIcon, LogOut, Hammer, UserCog, CalendarRange, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/common";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const BASE_NAV = [
  { to: "/coach", icon: LayoutDashboard, label: "Dashboard", end: true, testId: "cnav-dashboard" },
  { to: "/coach/customers", icon: Users, label: "Customers", testId: "cnav-customers" },
  { to: "/coach/builder", icon: Hammer, label: "Workout Builder", testId: "cnav-builder" },
  { to: "/coach/programs", icon: CalendarRange, label: "Programs", testId: "cnav-programs" },
  { to: "/coach/exercises", icon: Library, label: "Exercises", testId: "cnav-exercises" },
  { to: "/coach/messages", icon: MessageSquare, label: "Messages", testId: "cnav-messages" },
  { to: "/coach/checkins", icon: ClipboardCheck, label: "Check-Ins", testId: "cnav-checkins" },
  { to: "/coach/reports", icon: BarChart3, label: "Reports", testId: "cnav-reports" },
  { to: "/coach/settings", icon: SettingsIcon, label: "Settings", testId: "cnav-settings" },
];
const ADMIN_NAV = { to: "/coach/coaches", icon: UserCog, label: "Coaches", testId: "cnav-coaches" };

function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const now = ctx.currentTime;
    [880, 1174.7].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(g);
      g.connect(ctx.destination);
      const t = now + i * 0.16;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      o.start(t);
      o.stop(t + 0.17);
    });
    setTimeout(() => ctx.close(), 700);
  } catch (e) { /* audio unavailable */ }
}

function useMessageAlerts() {
  const [unread, setUnread] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem("msg_muted") === "1");
  const prev = useRef(null);
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!active) return;
        const n = data.unread || 0;
        if (prev.current !== null && n > prev.current) {
          if (localStorage.getItem("msg_muted") !== "1") playChime();
          toast.message("New message", { description: `You have ${n} unread message${n > 1 ? "s" : ""}.` });
        }
        prev.current = n;
        setUnread(n);
      } catch (e) { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { active = false; clearInterval(iv); };
  }, []);
  const toggleMute = () => setMuted((m) => { const v = !m; localStorage.setItem("msg_muted", v ? "1" : "0"); return v; });
  return { unread, muted, toggleMute };
}

export default function CoachLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = (user.roles || [user.role]).includes("admin");
  const NAV = isAdmin ? [...BASE_NAV.slice(0, 2), ADMIN_NAV, ...BASE_NAV.slice(2)] : BASE_NAV;
  const { unread, muted, toggleMute } = useMessageAlerts();

  const navItem = (n, isActive) => (
    <>
      <n.icon className="w-[18px] h-[18px]" /> {n.label}
      {n.to === "/coach/messages" && unread > 0 && (
        <span data-testid="msg-unread-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center">{unread}</span>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <aside className="w-60 border-r border-border h-screen sticky top-0 hidden lg:flex flex-col p-4">
        <Logo size="sm" />
        <nav className="mt-8 flex-1 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-brand/15 text-brand" : "text-secondary hover:text-white hover:bg-white/5"}`}>
              {({ isActive }) => navItem(n, isActive)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border pt-3">
          <button data-testid="msg-mute-toggle" onClick={toggleMute} title={muted ? "Message sound off" : "Message sound on"} className="w-full flex items-center gap-3 px-3 py-2 mb-1 rounded-lg text-sm text-secondary hover:text-white hover:bg-white/5 transition-all">
            {muted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />} {muted ? "Sound off" : "Sound on"}
          </button>
          <div className="flex items-center gap-3 px-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-brand/20 flex items-center justify-center overflow-hidden">
              {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-brand">{user.name[0]}</span>}
            </div>
            <div className="min-w-0"><p className="text-sm font-semibold truncate">{user.name}</p><p className="text-xs text-tertiary capitalize">{(user.roles || [user.role]).join(" · ")}</p></div>
          </div>
          <button data-testid="coach-logout" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-secondary hover:text-brand hover:bg-brand/10 transition-all">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden glass border-b border-border px-4 h-24 flex items-center justify-between sticky top-0 z-50">
          <Logo size="sm" />
          <div className="flex items-center gap-3">
            <button data-testid="msg-mute-toggle-mobile" onClick={toggleMute} className="text-secondary relative">
              {muted ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
              {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
            </button>
            <button onClick={logout} className="text-secondary"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="lg:hidden flex overflow-x-auto gap-1 px-3 py-2 border-b border-border bg-surface">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${isActive ? "bg-brand/15 text-brand" : "text-secondary"}`}>
              {n.label}{n.to === "/coach/messages" && unread > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
            </NavLink>
          ))}
        </div>
        <main><Outlet /></main>
      </div>
    </div>
  );
}
